<template>
  <div
    v-if="isLoaded"
    ref="recContainer"
    class="product-recommendations-cards product-recommendations-cards--white"
  >
    <div class="product-recommendations-cards__content">
      <h1 class="product-recommendations-cards__title">
        {{ title || $t('title') }}
      </h1>
      <div
        class="product-recommendations-cards__products-wrap"
        :class="{ 'product-recommendations-cards__products-wrap--all-gender': !genderToShow }"
      >
        <div class="product-recommendations-cards__products">
          <Cards
            :cards="displayedItems"
            :context="$CONTEXTS.recommendation"
            :summaryIsHidden="true"
            :backgroundColor="CARD_COLORS.light"
            :genderToShow="genderToShow"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getProductsInProductImpressionFormat } from '@/helpers/product'
import Cards from '@/components/_shared/Cards/Cards.vue'
import { CARD_COLORS } from '@/components/_shared/Cards/Card/Card.vue'

export default {
  name: 'ProductRecommendationsCards',
  components: { Cards },

  props: {
    title: {
      type: String,
    },
    items: {
      type: Array,
    },
    genderToShow: {
      type: String,
    },
  },

  data() {
    return {
      CARD_COLORS,
      displayedItems: [],
      previousRecIds: [],
    }
  },

  computed: {
    ...mapGetters({
      vpWidth: 'viewport/width',
    }),

    isLoaded() {
      return !!this.items?.length
    },
  },

  watch: {
    vpWidth: 'setNumberOfCards',

    items: {
      immediate: true,
      handler(items) {
        if (!items.length) return
        this.setNumberOfCards()
        const changedItems = items.filter(item => !this.previousRecIds.includes(item.id))
        this.previousRecIds = items.map(item => item.id)
        if (changedItems.length) this.trackListView(changedItems)
      },
    },
  },

  mounted() {
    this.setNumberOfCards()
  },

  methods: {
    setNumberOfCards() {
      if (!this.isLoaded) return

      let numberOfItems = 3
      if (this.$refs.recContainer) {
        let containerWidth = this.$refs.recContainer.offsetWidth
        numberOfItems = containerWidth > 1555 ? 4 : containerWidth >= 1024 ? 3 : 2
      }
      this.displayedItems = this.items.slice(0, numberOfItems)
    },

    trackListView(items) {
      if (items.length) {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.listView,
          productData: {
            items: getProductsInProductImpressionFormat(items, this.$CONTEXTS.recommendation),
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/components/_shared/Cards/card-settings';

$card-max-width: 300px;
$card-bottom-box-min-height-mobile: 110px;
$card-container-padding-x-mobile: 60px;

.product-recommendations-cards {
  position: relative;
  padding: 20px 20px 40px;
  margin: 0 auto 0;
  color: $color-white;
  transition: height 0.5s ease-in-out;

  &--white {
    background-color: $color-white;
    color: $color-text;
  }

  @media (min-width: 700px) {
    margin: initial;
    padding-bottom: 50px;
  }

  @media (min-width: 800px) {
    padding: 30px 50px;
  }

  @media (min-width: 1000px) {
    padding: 30px 80px 75px;
  }

  // Recommendations block themeing
  /deep/ .cards {
    justify-content: center;

    // ::after selector is used for centering cards on grid e.g. filter page.
    &:after {
      content: none;
    }
  }

  /deep/ .card {
    background-color: $color-white;
    max-width: $card-max-width;
    margin: 20px 5px 0;

    &:hover {
      .card__bottom {
        background-color: rgba(255, 255, 255, 0.05);
      }
    }

    &__bottom {
      background-color: transparent;
    }

    @media (min-width: 700px) {
      flex-basis: 0;
      margin: 0 10px;
    }
  }

  &__title {
    text-align: center;
    margin-bottom: 40px;
    font-size: 28px;

    @media ($mq-large) {
      font-size: 36px;
    }

    @media ($mq-large) {
      margin-bottom: 50px;
    }
  }

  &__products-wrap {
    min-height: calc(50vw + #{$card-visual-margin-y-mobile + $card-bottom-box-min-height-mobile});

    @media (min-width: 500px) {
      min-height: calc(
        50vw + #{$card-visual-margin-y-mobile + $card-bottom-box-min-height-mobile -
          $card-container-padding-x-mobile}
      );
    }

    @media (min-width: $card-small-mobile-breakpoint) {
      min-height: $card-max-width + $card-visual-margin-y-desktop +
        $card-bottom-box-min-height-mobile;
    }

    @media ($card-mobile-breakpoint) {
      min-height: $card-max-width + $card-visual-margin-y-desktop +
        $card-bottom-box-single-gender-min-height;

      &--all-gender {
        min-height: $card-max-width + $card-visual-margin-y-desktop +
          $card-bottom-box-double-gender-min-height;
      }
    }
  }

  &__products {
    justify-content: center;

    @media (min-width: 500px) {
      padding: 0 $card-container-padding-x-mobile;
    }

    @media (min-width: 700px) {
      display: flex;
      padding: 0;
      margin: 0 auto;
    }

    @media (min-width: 2000px) {
      max-width: 1500px;
    }
  }
}
</style>

<i18n src="@/locales/product_recommendations.json">
</i18n>
